<template>
  <div class="jackpot-hex w-full flex flex-col items-center gap-3 sm:gap-5 py-8 px-2 sm:px-4 relative">
    
    <!-- Header / Logo -->
    <div class="text-center">
      <div class="flex items-center justify-center text-3xl sm:text-2xl md:text-3xl font-black italic tracking-widest drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
        <span class="text-cyan-400 mr-1 sm:mr-2">LUCKY</span>
        <div class="relative w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 bg-gradient-to-br from-red-500 to-red-700 rounded-full border-2 border-white flex items-center justify-center shadow-lg">
          <div class="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded-full border border-white border-dashed"></div>
          <div class="absolute w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full"></div>
        </div>
        <span class="text-white ml-1 sm:ml-2">SPIN</span>
      </div>
      <div class="mt-1 flex items-center justify-center">
        <div class="w-6 sm:w-8 h-[1px] bg-gray-400"></div>
        <span class="text-[8px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] text-gray-300 mx-1 sm:mx-2 uppercase">Casino · Online</span>
        <div class="w-6 sm:w-8 h-[1px] bg-gray-400"></div>
      </div>
    </div>

    <!-- GRAND JACKPOT - 100% width -->
    <div v-if="showGrand" :class="['relative w-full max-w-[950px] transition-all duration-500', grandHot && !grandWon ? 'jackpot-hot' : '', grandWon ? 'animate-win' : '']">
      <div class="absolute -top-3 sm:-top-5 left-1/2 -translate-x-1/2 z-20 text-base sm:text-xl md:text-3xl font-black tracking-wider sm:tracking-widest grand-title whitespace-nowrap">
        {{ grandWon ? 'GRAND WIN!' : 'GRAND JACKPOT' }}
      </div>
      
      <div class="w-full p-[3px] sm:p-[4px] bg-gradient-to-b from-[#fff7c2] via-[#d97706] to-[#fde047] clip-hex shadow-[0_10px_20px_rgba(0,0,0,0.6)] relative">
        <div v-if="grandHot && !grandWon" class="absolute top-6 right-2 sm:top-11 sm:right-8 bg-red-600 text-white text-[12px] sm:text-[24px] font-black px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border-2 border-yellow-400 z-50 animate-bounce shadow-lg">
          HOT!
        </div>
        <div class="sparkle sparkle-1 top-0 left-[15%]"></div>
        <div class="sparkle sparkle-2 bottom-0 right-[20%]"></div>
        <div class="sparkle sparkle-3 top-1/2 right-[2%]"></div>

        <div class="w-full h-14 sm:h-20 md:h-28 bg-gradient-to-r from-[#ca8a04] via-[#fef08a] to-[#ca8a04] clip-hex-inner flex items-center justify-center relative inner-bevel-gold overflow-hidden">
          <div class="absolute inset-0 opacity-20 stripe-animate" style="background-image: repeating-linear-gradient(45deg, transparent, transparent 2px, #fff 2px, #fff 4px); background-size: 40px 40px;"></div>
          <div class="shine-top"></div>
          <div class="shine-sweep"></div>
          <span class="font-numbers text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] drop-shadow-[0_4px_4px_rgba(0,0,0,0.8)] relative z-10 rolling-digits">
            <span v-for="(d, i) in getDigitArray(jackpots.grand, 'grand')" :key="'g'+i" class="digit-col" :class="{ 'digit-sep': !d.isDigit }">
              <span v-if="d.isDigit" class="digit-strip" :class="{ 'digit-snap': d.rollBack }" :style="{ transform: `translateY(${-parseInt(d.char)}em)` }">
                <span v-for="n in 10" :key="n" class="digit-cell">{{ n-1 }}</span>
              </span>
              <span v-else class="digit-cell">{{ d.char }}</span>
            </span>
          </span>
        </div>
      </div>
    </div>

    <!-- MAJOR JACKPOT - 92% width -->
    <div v-if="showMajor" :class="['relative w-[92%] sm:w-[90%] max-w-[850px] transition-all duration-500', majorHot && !majorWon ? 'jackpot-hot' : '', majorWon ? 'animate-win' : '']">
      <div class="w-full p-[2px] sm:p-[3px] bg-gradient-to-b from-[#fef08a] via-[#b45309] to-[#fde047] clip-hex shadow-[0_8px_16px_rgba(0,0,0,0.5)] relative">
        <div v-if="majorHot && !majorWon" class="absolute top-4 right-2 sm:top-8 sm:right-7 bg-red-600 text-white text-[10px] sm:text-[14px] font-black px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border-2 border-yellow-400 z-50 animate-bounce shadow-lg">
          HOT!
        </div>
        
        <div class="sparkle sparkle-1 top-1 left-[12%]"></div>
        <div class="sparkle sparkle-3 bottom-1 right-[15%]"></div>

        <div class="w-full h-12 sm:h-16 md:h-20 flex clip-hex-inner bg-black">
          <div class="w-[30%] sm:w-[32%] bg-gradient-to-b from-[#3b82f6] via-[#2563eb] to-[#1e3a8a] border-r-2 border-yellow-500 flex flex-col items-center justify-center leading-none relative inner-bevel-color">
            <div class="shine-top"></div>
            <div class="text-lg sm:text-2xl md:text-3xl font-black text-indigo-200 drop-shadow-md z-10">MAJOR</div>
            <div class="text-[7px] sm:text-[9px] md:text-sm font-black text-yellow-400 tracking-wider mt-0.5 z-10" style="-webkit-text-stroke: 0.5px #713f12;">JACKPOT</div>
          </div>
          <div class="w-[70%] sm:w-[68%] bg-gradient-to-b from-[#1d4ed8] via-[#1e40af] to-[#0f172a] flex items-center justify-center relative inner-bevel-color overflow-hidden">
            <div class="shine-top"></div>
            <div class="shine-sweep"></div>
            <span class="font-numbers text-3xl sm:text-4xl md:text-5xl lg:text-6xl drop-shadow-[0_3px_3px_rgba(0,0,0,0.9)] relative z-10 rolling-digits">
              <span v-for="(d, i) in getDigitArray(jackpots.major, 'major')" :key="'mj'+i" class="digit-col" :class="{ 'digit-sep': !d.isDigit }">
                <span v-if="d.isDigit" class="digit-strip" :class="{ 'digit-snap': d.rollBack }" :style="{ transform: `translateY(${-parseInt(d.char)}em)` }">
                  <span v-for="n in 10" :key="n" class="digit-cell">{{ n-1 }}</span>
                </span>
                <span v-else class="digit-cell">{{ d.char }}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- MINOR JACKPOT - 80% width -->
    <div v-if="showMinor" :class="['relative w-[80%] sm:w-[78%] max-w-[700px] transition-all duration-500', minorHot && !minorWon ? 'jackpot-hot' : '', minorWon ? 'animate-win' : '']">
      <div class="w-full p-[2px] sm:p-[3px] bg-gradient-to-b from-[#fef08a] via-[#b45309] to-[#fde047] clip-hex shadow-[0_8px_16px_rgba(0,0,0,0.5)] relative">
        <div v-if="minorHot && !minorWon" class="absolute top-4 right-2 sm:top-7 sm:right-6 bg-red-600 text-white text-[8px] sm:text-[12px] font-black px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border-2 border-yellow-400 z-50 animate-bounce shadow-lg">
          HOT!
        </div>
        
        <div class="sparkle sparkle-2 top-1 left-[10%]"></div>
        <div class="sparkle sparkle-1 bottom-1 right-[12%]"></div>

        <div class="w-full h-11 sm:h-14 md:h-[4.5rem] flex clip-hex-inner bg-black">
          <div class="w-[30%] sm:w-[32%] bg-gradient-to-b from-[#4ade80] via-[#16a34a] to-[#14532d] border-r-2 border-yellow-500 flex flex-col items-center justify-center leading-none relative inner-bevel-color">
            <div class="shine-top"></div>
            <div class="text-base sm:text-xl md:text-2xl font-black text-green-200 drop-shadow-md z-10">MINOR</div>
            <div class="text-[6px] sm:text-[8px] md:text-xs font-black text-yellow-400 tracking-wider mt-0.5 z-10" style="-webkit-text-stroke: 0.5px #713f12;">JACKPOT</div>
          </div>
          <div class="w-[70%] sm:w-[68%] bg-gradient-to-b from-[#15803d] via-[#166534] to-[#052e16] flex items-center justify-center relative inner-bevel-color overflow-hidden">
            <div class="shine-top"></div>
            <div class="shine-sweep"></div>
            <span class="font-numbers text-2xl sm:text-3xl md:text-4xl lg:text-5xl drop-shadow-[0_3px_3px_rgba(0,0,0,0.9)] relative z-10 rolling-digits">
              <span v-for="(d, i) in getDigitArray(jackpots.minor, 'minor')" :key="'mn'+i" class="digit-col" :class="{ 'digit-sep': !d.isDigit }">
                <span v-if="d.isDigit" class="digit-strip" :class="{ 'digit-snap': d.rollBack }" :style="{ transform: `translateY(${-parseInt(d.char)}em)` }">
                  <span v-for="n in 10" :key="n" class="digit-cell">{{ n-1 }}</span>
                </span>
                <span v-else class="digit-cell">{{ d.char }}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- MINI JACKPOT - 68% width -->
    <div v-if="showMini" :class="['relative w-[68%] sm:w-[65%] max-w-[550px] transition-all duration-500', miniHot && !miniWon ? 'jackpot-hot' : '', miniWon ? 'animate-win' : '']">
      <div class="w-full p-[2px] sm:p-[3px] bg-gradient-to-b from-[#fef08a] via-[#b45309] to-[#fde047] clip-hex shadow-[0_8px_16px_rgba(0,0,0,0.5)] relative">
        <div v-if="miniHot && !miniWon" class="absolute top-4 right-2 sm:top-6 sm:right-6 bg-red-600 text-white text-[6px] sm:text-[10px] font-black px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border-2 border-yellow-400 z-50 animate-bounce shadow-lg">
          HOT!
        </div>
        
        <div class="sparkle sparkle-3 top-1 left-[8%]"></div>
        <div class="sparkle sparkle-2 bottom-1 right-[10%]"></div>

        <div class="w-full h-10 sm:h-12 md:h-16 flex clip-hex-inner bg-black">
          <div class="w-[30%] sm:w-[32%] bg-gradient-to-b from-[#f87171] via-[#dc2626] to-[#7f1d1d] border-r-2 border-yellow-500 flex flex-col items-center justify-center leading-none relative inner-bevel-color">
            <div class="shine-top"></div>
            <div class="text-sm sm:text-lg md:text-xl font-black text-red-200 drop-shadow-md z-10">MINI</div>
            <div class="text-[5px] sm:text-[7px] md:text-[10px] font-black text-yellow-400 tracking-wider mt-0.5 z-10" style="-webkit-text-stroke: 0.5px #713f12;">JACKPOT</div>
          </div>
          <div class="w-[70%] sm:w-[68%] bg-gradient-to-b from-[#b91c1c] via-[#991b1b] to-[#450a0a] flex items-center justify-center relative inner-bevel-color overflow-hidden">
            <div class="shine-top"></div>
            <div class="shine-sweep"></div>
            <span class="font-numbers text-xl sm:text-2xl md:text-3xl lg:text-4xl drop-shadow-[0_3px_3px_rgba(0,0,0,0.9)] relative z-10 rolling-digits">
              <span v-for="(d, i) in getDigitArray(jackpots.mini, 'mini')" :key="'mi'+i" class="digit-col" :class="{ 'digit-sep': !d.isDigit }">
                <span v-if="d.isDigit" class="digit-strip" :class="{ 'digit-snap': d.rollBack }" :style="{ transform: `translateY(${-parseInt(d.char)}em)` }">
                  <span v-for="n in 10" :key="n" class="digit-cell">{{ n-1 }}</span>
                </span>
                <span v-else class="digit-cell">{{ d.char }}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- FULL-COMPONENT WIN CELEBRATION OVERLAY -->
    <Transition name="cele">
      <div v-if="activeWinInfo" class="cele-overlay" :key="activeWinInfo.tier">
        <!-- Dark backdrop -->
        <div class="cele-backdrop"></div>
        <!-- Rotating light rays -->
        <div class="cele-rays"></div>
        <!-- Expanding rings -->
        <div class="cele-rings">
          <span></span><span></span><span></span>
        </div>
        <!-- Particle explosion — 20 particles -->
        <div class="cele-particles">
          <span v-for="n in 20" :key="n" class="cele-particle" :style="`--i: ${n}`"></span>
        </div>
        <!-- Win text -->
        <div class="cele-content">
          <div class="cele-label">{{ activeWinInfo.label }}</div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  grandJackpot: { type: Number, default: null },
  majorJackpot: { type: Number, default: null },
  minorJackpot: { type: Number, default: null },
  miniJackpot: { type: Number, default: null },
  // Win targets - triggers win when reached
  grandWinTarget: { type: Number, default: null },
  majorWinTarget: { type: Number, default: null },
  minorWinTarget: { type: Number, default: null },
  miniWinTarget: { type: Number, default: null },
  // Base values for reset after win
  grandBaseValue: { type: Number, default: null },
  majorBaseValue: { type: Number, default: null },
  minorBaseValue: { type: Number, default: null },
  miniBaseValue: { type: Number, default: null },
  // Hot state
  grandHot: { type: Boolean, default: false },
  majorHot: { type: Boolean, default: false },
  minorHot: { type: Boolean, default: false },
  miniHot: { type: Boolean, default: false },
  // Drop: when true
  grandDrop: { type: Boolean, default: false },
  majorDrop: { type: Boolean, default: false },
  minorDrop: { type: Boolean, default: false },
  miniDrop: { type: Boolean, default: false },
  // Explicit drop/win amounts (the actual amount when the jackpot dropped)
  grandDropAmount: { type: Number, default: null },
  majorDropAmount: { type: Number, default: null },
  minorDropAmount: { type: Number, default: null },
  miniDropAmount: { type: Number, default: null },
  // Auto increment
  autoIncrement: { type: Boolean, default: true },
  incrementInterval: { type: Number, default: 100 },
  // Which tiers exist by name in API data (show row only if present)
  visibleTiers: { type: Object, default: null }
})

const emit = defineEmits(['win', 'update'])

const jackpots = reactive({
  grand: props.grandJackpot,
  major: props.majorJackpot,
  minor: props.minorJackpot,
  mini: props.miniJackpot
})

const showGrand = computed(() => props.visibleTiers ? props.visibleTiers.grand : true)
const showMajor = computed(() => props.visibleTiers ? props.visibleTiers.major : true)
const showMinor = computed(() => props.visibleTiers ? props.visibleTiers.minor : true)
const showMini = computed(() => props.visibleTiers ? props.visibleTiers.mini : true)

const grandWon = ref(false)
const majorWon = ref(false)
const minorWon = ref(false)
const miniWon = ref(false)

const activeWinInfo = ref(null)

// Track the peak (highest) jackpot value per tier — used as the win amount when a drop triggers
const peakAmounts = reactive({
  grand: props.grandJackpot ?? 0,
  major: props.majorJackpot ?? 0,
  minor: props.minorJackpot ?? 0,
  mini: props.miniJackpot ?? 0
})


const prevDigitStrings = {}

const getDigitArray = (value, tier) => {
  if (value == null) return []
  const str = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const prev = prevDigitStrings[tier] || str

  const result = str.split('').map((ch, i) => {
    const isDigit = /\d/.test(ch)
    if (!isDigit) return { char: ch, isDigit: false, rollBack: false }
    const curr = parseInt(ch)
    const prevCh = prev[i]
    const prevD = prevCh && /\d/.test(prevCh) ? parseInt(prevCh) : curr
    return { char: ch, isDigit: true, rollBack: curr < prevD }
  })

  prevDigitStrings[tier] = str
  return result
}

const triggerWin = (tier, winAmount) => {
  const winRefs = { grand: grandWon, major: majorWon, minor: minorWon, mini: miniWon }
  const baseValues = {
    grand: props.grandBaseValue,
    major: props.majorBaseValue,
    minor: props.minorBaseValue,
    mini: props.miniBaseValue
  }

  const winRef = winRefs[tier]
  if (winRef && !winRef.value) {
    winRef.value = true
    const amount = winAmount ?? jackpots[tier]
    activeWinInfo.value = { tier, label: tier.toUpperCase() + ' JACKPOT WIN!', amount }
    emit('win', { tier, amount })

    setTimeout(() => {
      jackpots[tier] = baseValues[tier]
      winRef.value = false
      if (activeWinInfo.value && activeWinInfo.value.tier === tier) {
        activeWinInfo.value = null
      }
    }, 10000)
  }
}

const checkWinTargets = () => {
  if (props.grandWinTarget && jackpots.grand >= props.grandWinTarget && !grandWon.value) {
    triggerWin('grand')
  }
  if (props.majorWinTarget && jackpots.major >= props.majorWinTarget && !majorWon.value) {
    triggerWin('major')
  }
  if (props.minorWinTarget && jackpots.minor >= props.minorWinTarget && !minorWon.value) {
    triggerWin('minor')
  }
  if (props.miniWinTarget && jackpots.mini >= props.miniWinTarget && !miniWon.value) {
    triggerWin('mini')
  }
}

let interval = null
const updateJackpots = () => {
  if (!grandWon.value) jackpots.grand += Math.random() * 0.5
  if (!majorWon.value) jackpots.major += Math.random() * 1.5 // Faster for demo
  if (!minorWon.value) jackpots.minor += Math.random() * 0.3
  if (!miniWon.value) jackpots.mini += Math.random() * 0.1

  // Update peak values after increment so we always capture the highest amount
  peakAmounts.grand = Math.max(peakAmounts.grand, jackpots.grand)
  peakAmounts.major = Math.max(peakAmounts.major, jackpots.major)
  peakAmounts.minor = Math.max(peakAmounts.minor, jackpots.minor)
  peakAmounts.mini = Math.max(peakAmounts.mini, jackpots.mini)
  
  checkWinTargets()
  emit('update', { ...jackpots })
}

onMounted(() => {
  if (props.autoIncrement) {
    interval = setInterval(updateJackpots, props.incrementInterval)
  }
})

onUnmounted(() => {
  if (interval) clearInterval(interval)
})

watch(() => props.grandJackpot, (val) => { peakAmounts.grand = Math.max(peakAmounts.grand, val ?? 0); jackpots.grand = val })
watch(() => props.majorJackpot, (val) => { peakAmounts.major = Math.max(peakAmounts.major, val ?? 0); jackpots.major = val })
watch(() => props.minorJackpot, (val) => { peakAmounts.minor = Math.max(peakAmounts.minor, val ?? 0); jackpots.minor = val })
watch(() => props.miniJackpot, (val) => { peakAmounts.mini = Math.max(peakAmounts.mini, val ?? 0); jackpots.mini = val })

watch(() => props.grandDrop, (val) => { if (val) triggerWin('grand', props.grandDropAmount || peakAmounts.grand || jackpots.grand) })
watch(() => props.majorDrop, (val) => { if (val) triggerWin('major', props.majorDropAmount || peakAmounts.major || jackpots.major) })
watch(() => props.minorDrop, (val) => { if (val) triggerWin('minor', props.minorDropAmount || peakAmounts.minor || jackpots.minor) })
watch(() => props.miniDrop, (val) => { if (val) triggerWin('mini', props.miniDropAmount || peakAmounts.mini || jackpots.mini) })

defineExpose({ triggerWin, jackpots })
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,700;0,900;1,900&display=swap');

.jackpot-hex {
  font-family: 'Montserrat', sans-serif;
  user-select: none;
}

/* Hexagon Clip Paths - Mobile */
.clip-hex {
  clip-path: polygon(15px 0, calc(100% - 15px) 0, 100% 50%, calc(100% - 15px) 100%, 15px 100%, 0 50%);
}
.clip-hex-inner {
  clip-path: polygon(13px 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 13px 100%, 0 50%);
}

@media (min-width: 640px) {
  .clip-hex {
    clip-path: polygon(25px 0, calc(100% - 25px) 0, 100% 50%, calc(100% - 25px) 100%, 25px 100%, 0 50%);
  }
  .clip-hex-inner {
    clip-path: polygon(23px 0, calc(100% - 23px) 0, 100% 50%, calc(100% - 23px) 100%, 23px 100%, 0 50%);
  }
}

@media (min-width: 768px) {
  .clip-hex {
    clip-path: polygon(35px 0, calc(100% - 35px) 0, 100% 50%, calc(100% - 35px) 100%, 35px 100%, 0 50%);
  }
  .clip-hex-inner {
    clip-path: polygon(33px 0, calc(100% - 33px) 0, 100% 50%, calc(100% - 33px) 100%, 33px 100%, 0 50%);
  }
}

.font-numbers {
  font-family: 'Impact', 'Arial Black', sans-serif;
  letter-spacing: 0.5px;
}

.text-gold-gradient {
  background: linear-gradient(to bottom, #fffbeb 0%, #fde047 25%, #eab308 50%, #a16207 75%, #fef08a 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.grand-title {
  background: linear-gradient(to bottom, #ef4444 0%, #991b1b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-text-stroke: 1px #fde047;
  filter: drop-shadow(0px 3px 2px rgba(0,0,0,0.8));
}

@media (min-width: 640px) {
  .grand-title { -webkit-text-stroke: 1.5px #fde047; }
}

.inner-bevel-gold {
  box-shadow: inset 0px 4px 8px rgba(255,255,255,0.6), inset 0px -4px 8px rgba(0,0,0,0.4);
}
.inner-bevel-color {
  box-shadow: inset 0px 3px 5px rgba(255,255,255,0.3), inset 0px -3px 5px rgba(0,0,0,0.5);
}

.shine-top {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 40%;
  background: linear-gradient(to bottom, rgba(255,255,255,0.3) 0%, transparent 100%);
  pointer-events: none;
}

.sparkle {
  position: absolute;
  width: 3px; height: 3px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 0 8px 3px rgba(255,255,255,0.8), 0 0 16px 6px rgba(255,255,255,0.4);
  z-index: 30;
  opacity: 0;
}

/* Sparkle twinkling */
@keyframes sparkle-twinkle {
  0%, 100% { opacity: 0; transform: scale(0.3); }
  50% { opacity: 1; transform: scale(1.2); }
}
.sparkle-1 { animation: sparkle-twinkle 2s infinite ease-in-out; }
.sparkle-2 { animation: sparkle-twinkle 2s 0.7s infinite ease-in-out; }
.sparkle-3 { animation: sparkle-twinkle 2s 1.4s infinite ease-in-out; }

/* Sweeping light bar across number area */
@keyframes shine-sweep-move {
  0% { left: -30%; }
  100% { left: 130%; }
}
.shine-sweep {
  position: absolute;
  top: 0; bottom: 0;
  width: 20%;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.15) 60%, transparent 100%);
  z-index: 5;
  pointer-events: none;
  animation: shine-sweep-move 3s 1s infinite ease-in-out;
}

/* Moving diagonal stripes on Grand */
@keyframes stripe-scroll {
  0% { background-position: 0 0; }
  100% { background-position: 40px 40px; }
}
.stripe-animate {
  animation: stripe-scroll 2s linear infinite;
}

/* Gold border shimmer */
@keyframes border-shimmer {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.2); }
}
.clip-hex {
  animation: border-shimmer 3s infinite ease-in-out;
}

@keyframes jackpot-pulse {
  0%, 100% { filter: drop-shadow(0 0 15px rgba(239, 68, 68, 0.8)) brightness(1); }
  50% { filter: drop-shadow(0 0 35px rgba(254, 240, 138, 0.9)) brightness(1.3); transform: scale(1.02); }
}
.jackpot-hot {
  animation: jackpot-pulse 1.5s infinite ease-in-out;
  z-index: 50;
}

/* === WIN / DROP CELEBRATION === */

/* Phase 1: Quick shake burst (plays once) */
@keyframes win-shake {
  0% { transform: translateX(0); }
  10% { transform: translateX(-6px) rotate(-0.5deg); }
  20% { transform: translateX(6px) rotate(0.5deg); }
  30% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  50% { transform: translateX(-3px); }
  60% { transform: translateX(3px); }
  80% { transform: translateX(-1px); }
  100% { transform: translateX(0); }
}

/* Phase 2: Ongoing dramatic glow pulse */
@keyframes win-glow-loop {
  0% { transform: scale(1); filter: brightness(1.2) drop-shadow(0 0 20px rgba(253,224,71,0.6)); }
  50% { transform: scale(1.06); filter: brightness(1.6) drop-shadow(0 0 60px rgba(253,224,71,0.9)) drop-shadow(0 0 120px rgba(234,88,12,0.4)); }
  100% { transform: scale(1); filter: brightness(1.2) drop-shadow(0 0 20px rgba(253,224,71,0.6)); }
}

.animate-win {
  animation: win-shake 0.6s ease-out, win-glow-loop 1.2s 0.6s ease-in-out infinite;
  z-index: 100 !important;
}

/* === FULL-COMPONENT CELEBRATION OVERLAY === */
.cele-overlay {
  position: absolute;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  overflow: hidden;
}

/* Transition enter/leave */
.cele-enter-active { transition: opacity 0.3s ease-out; }
.cele-leave-active { transition: opacity 0.8s ease-in; }
.cele-enter-from, .cele-leave-to { opacity: 0; }

/* Dark semi-transparent backdrop */
.cele-backdrop {
  position: absolute;
  inset: -20px;
  background: radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%);
  animation: cele-backdrop-in 0.4s ease-out both;
}
@keyframes cele-backdrop-in {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

/* Big rotating golden light rays */
.cele-rays {
  position: absolute;
  width: 200%; height: 200%;
  top: -50%; left: -50%;
  pointer-events: none;
  opacity: 0;
  background: conic-gradient(
    from 0deg,
    transparent 0deg, rgba(253,224,71,0.2) 6deg, transparent 12deg,
    transparent 18deg, rgba(253,224,71,0.15) 24deg, transparent 30deg,
    transparent 36deg, rgba(253,224,71,0.2) 42deg, transparent 48deg,
    transparent 54deg, rgba(253,224,71,0.15) 60deg, transparent 66deg,
    transparent 72deg, rgba(253,224,71,0.2) 78deg, transparent 84deg,
    transparent 90deg, rgba(253,224,71,0.15) 96deg, transparent 102deg,
    transparent 108deg, rgba(253,224,71,0.2) 114deg, transparent 120deg,
    transparent 126deg, rgba(253,224,71,0.15) 132deg, transparent 138deg,
    transparent 144deg, rgba(253,224,71,0.2) 150deg, transparent 156deg,
    transparent 162deg, rgba(253,224,71,0.15) 168deg, transparent 174deg,
    transparent 180deg, rgba(253,224,71,0.2) 186deg, transparent 192deg,
    transparent 198deg, rgba(253,224,71,0.15) 204deg, transparent 210deg,
    transparent 216deg, rgba(253,224,71,0.2) 222deg, transparent 228deg,
    transparent 234deg, rgba(253,224,71,0.15) 240deg, transparent 246deg,
    transparent 252deg, rgba(253,224,71,0.2) 258deg, transparent 264deg,
    transparent 270deg, rgba(253,224,71,0.15) 276deg, transparent 282deg,
    transparent 288deg, rgba(253,224,71,0.2) 294deg, transparent 300deg,
    transparent 306deg, rgba(253,224,71,0.15) 312deg, transparent 318deg,
    transparent 324deg, rgba(253,224,71,0.2) 330deg, transparent 336deg,
    transparent 342deg, rgba(253,224,71,0.15) 348deg, transparent 354deg,
    transparent 360deg
  );
  animation: cele-rays-in 0.6s ease-out forwards, cele-rays-spin 12s 0.6s linear infinite;
}
@keyframes cele-rays-in {
  0% { opacity: 0; transform: scale(0.3) rotate(0deg); }
  100% { opacity: 0.9; transform: scale(1) rotate(15deg); }
}
@keyframes cele-rays-spin {
  0% { transform: rotate(15deg); }
  100% { transform: rotate(375deg); }
}

/* Expanding ring pulses */
.cele-rings {
  position: absolute;
  top: 50%; left: 50%;
  width: 0; height: 0;
}
.cele-rings span {
  position: absolute;
  top: 0; left: 0;
  width: 60px; height: 60px;
  margin: -30px 0 0 -30px;
  border-radius: 50%;
  border: 3px solid rgba(253,224,71,0.7);
  animation: cele-ring-expand 2.5s ease-out infinite;
}
.cele-rings span:nth-child(2) { animation-delay: 0.8s; }
.cele-rings span:nth-child(3) { animation-delay: 1.6s; }

@keyframes cele-ring-expand {
  0% { transform: scale(0.5); opacity: 1; border-width: 4px; }
  100% { transform: scale(10); opacity: 0; border-width: 1px; }
}

/* 20 exploding particles — bigger & further */
.cele-particles {
  position: absolute;
  top: 50%; left: 50%;
  width: 0; height: 0;
}
.cele-particle {
  position: absolute;
  width: 10px; height: 10px;
  border-radius: 50%;
  background: radial-gradient(circle, #fef08a, #eab308);
  box-shadow: 0 0 8px 3px rgba(253,224,71,0.6);
  animation: cele-particle-fly 2s ease-out infinite;
  animation-delay: calc(var(--i) * 0.1s);
}
.cele-particle:nth-child(even) {
  width: 7px; height: 7px;
  background: radial-gradient(circle, #fff, #fde047);
  animation-duration: 1.6s;
}
.cele-particle:nth-child(3n) {
  width: 5px; height: 5px;
  background: radial-gradient(circle, #fb923c, #ea580c);
  animation-duration: 2.4s;
}
.cele-particle:nth-child(5n) {
  width: 12px; height: 12px;
  background: radial-gradient(circle, #fef9c3, #fbbf24);
  box-shadow: 0 0 12px 4px rgba(253,224,71,0.8);
  animation-duration: 2s;
}

@keyframes cele-particle-fly {
  0% {
    transform: rotate(calc(var(--i) * 18deg)) translateX(0) scale(1);
    opacity: 1;
  }
  50% { opacity: 1; }
  100% {
    transform: rotate(calc(var(--i) * 18deg)) translateX(min(260px, 40vw)) scale(0);
    opacity: 0;
  }
}

/* Centered win text + amount */
.cele-content {
  position: relative;
  z-index: 10;
  text-align: center;
  animation: cele-content-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes cele-content-in {
  0% { transform: scale(0) rotate(-8deg); opacity: 0; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.cele-label {
  font-family: 'Montserrat', sans-serif;
  font-weight: 900;
  font-style: italic;
  text-transform: uppercase;
  font-size: clamp(1.5rem, 5vw, 3.5rem);
  letter-spacing: -0.02em;
  color: #fef08a;
  -webkit-text-fill-color: #fef08a;
  -webkit-text-stroke: 2.5px #ea580c;
  paint-order: stroke fill;
  text-shadow: 0 0 30px rgba(253,224,71,0.9), 0 0 60px rgba(234,88,12,0.5), 0 4px 8px rgba(0,0,0,0.6);
  animation: cele-text-pulse 2s 0.6s ease-in-out infinite;
  line-height: 1.2;
}

.cele-amount {
  font-size: clamp(1.2rem, 4vw, 2.8rem);
  margin-top: 4px;
  background: linear-gradient(to bottom, #fffbeb 0%, #fde047 30%, #eab308 60%, #fef08a 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 3px 6px rgba(0,0,0,0.8));
  animation: cele-text-pulse 2s 0.8s ease-in-out infinite;
  line-height: 1.2;
}

@keyframes cele-text-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}

/* Rolling odometer digits */
.rolling-digits {
  display: inline-flex;
  align-items: flex-start;
  line-height: 1;
}
.digit-col {
  display: inline-block;
  height: 1em;
  overflow: hidden;
  line-height: 1;
}
.digit-sep {
  width: 0.3em;
  text-align: center;
}
.digit-strip {
  display: block;
  transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  will-change: transform;
}
.digit-strip.digit-snap {
  transition: transform 0.05s linear;
}
.digit-cell {
  display: block;
  height: 1em;
  line-height: 1;
  text-align: center;
  background: linear-gradient(to bottom, #fffbeb 0%, #fde047 25%, #eab308 50%, #a16207 75%, #fef08a 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

</style>
